import React from "react";

const NA = "Не указано";
export const SERVICE_RU = { pax: "PAX", kitchen: "Кухня", other: "Другая мебель" };
export const TIME_RU = { any: "Любое время", morning: "Утро", afternoon: "День", evening: "Вечер" };
export const SOURCE_RU = { upload: "Загрузка файла (AI)", manual: "Заполнено вручную" };

const LAYOUT_RU = { straight: "Прямая", l: "Г-образная", u: "П-образная", island: "С островом", peninsula: "Полуостров", other: "Другая" };
const KITCHEN_FIELDS = [
  ["base_cabinets", "Нижние шкафы", "шт."], ["wall_cabinets", "Верхние шкафы", "шт."],
  ["tall_cabinets", "Высокие шкафы (пеналы)", "шт."], ["corner_cabinets", "Угловые шкафы", "шт."],
  ["worktop", "Столешницы", "шт."], ["worktop_length_cm", "Длина столешницы", "см"],
  ["sink", "Мойка", "шт."], ["oven", "Духовка", "шт."], ["hob", "Варочная панель", "шт."],
  ["dishwasher", "Посудомоечная машина", "шт."], ["fridge", "Холодильник/морозильник", "шт."],
  ["hood", "Вытяжка", "шт."], ["cover_panels", "Декоративные/боковые панели", "шт."],
  ["plinth", "Цоколь", "шт."], ["island", "Кухонный остров", "шт."], ["lighting", "Освещение", "шт."],
  ["other_unknown", "Прочее / неизвестное", "шт."],
];
const YN = { optYes: "Да", optNo: "Нет", optNotSure: "Клиент не уверен" };
const QUESTIONS = [
  ["old_kitchen_removed", "Демонтаж старой кухни", { optYes: "Да, нужно демонтировать старую кухню", optNo: "Нет, демонтаж не нужен", optNoOld: "Старой кухни нет", optNotSure: "Клиент не уверен" }],
  ["worktop_installation", "Монтаж столешницы", YN],
  ["sink_cutout", "Вырез под мойку", YN],
  ["hob_cutout", "Вырез под варочную панель", YN],
  ["walls_ready", "Стены готовы (покрашены/подготовлены)", YN],
];
const KNOWN_KITCHEN = new Set(["layout", "answers", ...KITCHEN_FIELDS.map((f) => f[0])]);

const isEmpty = (v) => v === undefined || v === null || v === "" || v === "unknown";
const fmt = (v, unit) => {
  if (isEmpty(v)) return NA;
  const n = Number(v);
  return Number.isFinite(n) && String(v).trim() !== "" ? `${n} ${unit}` : String(v);
};

function Row({ label, value, testId, muted }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-stone-100 last:border-0" data-testid={testId}>
      <span className="text-stone-500 min-w-0 break-words">{label}</span>
      <span className={`text-right font-medium break-words min-w-0 ${muted ? "text-stone-400" : "text-stone-800"}`}>{value}</span>
    </div>
  );
}

export function KitchenView({ config = {} }) {
  const answers = config.answers && typeof config.answers === "object" ? config.answers : {};
  const layout = isEmpty(config.layout) ? NA : (LAYOUT_RU[config.layout] || String(config.layout));
  return (
    <section className="bg-stone-50 rounded-lg p-3 space-y-3" data-testid="lead-kitchen-view">
      <div>
        <div className="text-xs text-stone-400 uppercase mb-1">Состав кухни</div>
        <Row label="Планировка" value={layout} muted={layout === NA} testId="kitchen-row-layout" />
        {KITCHEN_FIELDS.map(([k, label, unit]) => {
          const v = fmt(config[k], unit);
          return <Row key={k} label={label} value={v} muted={v === NA} testId={`kitchen-row-${k}`} />;
        })}
      </div>
      <div>
        <div className="text-xs text-stone-400 uppercase mb-1">Ответы клиента</div>
        {QUESTIONS.map(([k, label, opts]) => {
          const raw = answers[k];
          const v = isEmpty(raw) ? NA : (opts[raw] || String(raw));
          return <Row key={k} label={label} value={v} muted={v === NA} testId={`kitchen-answer-${k}`} />;
        })}
        <Row label="Техника для установки" value={isEmpty(answers.appliances_to_install) ? NA : String(answers.appliances_to_install)}
          muted={isEmpty(answers.appliances_to_install)} testId="kitchen-answer-appliances" />
      </div>
    </section>
  );
}

export function OtherView({ config = {} }) {
  const products = Array.isArray(config.products) ? config.products : [];
  return (
    <section className="bg-stone-50 rounded-lg p-3" data-testid="lead-other-view">
      <div className="text-xs text-stone-400 uppercase mb-2">Товары</div>
      {products.length === 0 ? <p className="text-stone-400">{NA}</p> : (
        <ul className="space-y-2">
          {products.map((p, i) => {
            const art = isEmpty(p.article) ? "" : String(p.article).trim();
            return (
              <li key={i} className="bg-white border border-stone-200 rounded-lg p-2.5" data-testid={`other-product-row-${i}`}>
                <div className="font-medium text-stone-800 break-words">{isEmpty(p.name) ? NA : String(p.name)}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-600 mt-1">
                  <span>Артикул: <b className="text-stone-800">{art || NA}</b></span>
                  <span>Количество: <b className="text-stone-800">{fmt(p.qty ?? p.quantity, "шт.")}</b></span>
                  {art && (
                    <a href={`https://www.ikea.com/dk/da/search/?q=${encodeURIComponent(art)}`} target="_blank" rel="noopener noreferrer"
                      className="text-[#1E3A2F] underline" data-testid={`other-product-ikea-link-${i}`}>Найти на IKEA.dk</a>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function TechDetails({ detail }) {
  const a = detail.attribution || {};
  const cfg = detail.configuration || {};
  const unknown = detail.service_type === "kitchen" ? Object.keys(cfg).filter((k) => !KNOWN_KITCHEN.has(k)) : [];
  return (
    <details className="bg-stone-50 rounded-lg p-3" data-testid="lead-tech-details">
      <summary className="cursor-pointer text-xs text-stone-500 uppercase font-semibold" data-testid="lead-tech-details-toggle">Технические данные</summary>
      <div className="mt-2 space-y-2 text-xs">
        {["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "initial_landing_page", "initial_referrer"].map((k) => (
          <Row key={k} label={k} value={a[k] || "—"} />
        ))}
        {unknown.length > 0 && <Row label="Прочие поля" value={unknown.join(", ")} testId="lead-tech-unknown-keys" />}
        <pre className="text-stone-700 whitespace-pre-wrap break-all font-mono bg-white border border-stone-200 rounded p-2">{JSON.stringify(cfg, null, 2)}</pre>
      </div>
    </details>
  );
}
